/**
 * Admin SPA WebSocket client — Wave 2.38 FE (TASK-106a).
 *
 * Wraps `socket.io-client` v4 around the server gateway shipped in Wave 2.34
 * BE-2 (`apps/api/src/websockets/admin.gateway.ts`):
 *   - Namespace `/admin`
 *   - Transport `websocket` only (long-polling fallback intentionally off so
 *     a misconfigured proxy cannot silently downgrade — matches server side).
 *   - JWT supplied in `socket.handshake.auth.token`; the gateway joins
 *     `user:`, `account:` and `system:alerts` rooms server-side from the
 *     validated claims.
 *
 * Token rotation: the `<SocketProvider>` recreates the socket whenever the
 * access token changes (single-flight refresh in `api-client.ts` updates the
 * token via `setAccessToken`, which re-renders subscribers of `useAuth()`).
 * Recreating is simpler than mutating `socket.auth + socket.connect()` and
 * avoids reasoning about stale closures inside the underlying client.
 *
 * Connection status is exposed so a topbar indicator (or follow-up UI) can
 * read it without subscribing to socket events directly.
 */

import {
  createContext,
  useContext,
} from 'react';
import type { Socket } from 'socket.io-client';

export type SocketConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';

export interface SocketContextValue {
  /** The active socket instance, or `null` when the user is unauthenticated. */
  socket: Socket | null;
  /** Connection lifecycle marker for UI indicators. */
  status: SocketConnectionStatus;
}

export const SocketContext = createContext<SocketContextValue | null>(null);

/**
 * Resolve the namespaced URL the admin SPA should hand to `io()`.
 *
 * The gateway listens on the same origin as the HTTP API (Nest mounts both),
 * so we reuse `VITE_API_BASE_URL`. When the base URL is empty (typical local
 * dev with a Vite proxy) we pass `/admin` so the client connects to the
 * current origin's `/admin` namespace via the dev server.
 */
export function resolveSocketUrl(baseUrl: string): string {
  const trimmed = baseUrl.trim().replace(/\/$/, '');
  return `${trimmed}/admin`;
}

export function useSocket(): SocketContextValue {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    throw new Error('useSocket must be used inside <SocketProvider>');
  }
  return ctx;
}
